import React from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface LoopConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const LoopConfig: React.FC<LoopConfigProps> = ({ node, updateConfig }) => {
  return (
    <div className="space-y-4">
      <ConfigSection title="Iteráció beállítások">
        <ConfigField
          label="Iteráció típusa"
          description="Hogyan történjen az iteráció"
        >
          <select
            value={node.data.config?.loopType || 'array'}
            onChange={(e) => updateConfig('loopType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="array">Tömb elemein</option>
            <option value="count">Számláló (N-szer)</option>
            <option value="while">Feltétel alapján (while)</option>
          </select>
        </ConfigField>

        {node.data.config?.loopType === 'array' && (
          <ConfigField
            label="Tömb változó"
            description="A bejárni kívánt tömb változó neve"
          >
            <input
              type="text"
              value={node.data.config?.arrayVariable || ''}
              onChange={(e) => updateConfig('arrayVariable', e.target.value)}
              placeholder="{{items}} vagy items"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </ConfigField>
        )}

        {node.data.config?.loopType === 'count' && (
          <ConfigField
            label="Ismétlések száma"
            description="Hányszor fusson le az iteráció"
          >
            <input
              type="number"
              value={node.data.config?.maxIterations || 10}
              onChange={(e) => updateConfig('maxIterations', parseInt(e.target.value))}
              min="1"
              max="1000"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </ConfigField>
        )}

        {node.data.config?.loopType === 'while' && (
          <ConfigField
            label="Feltétel"
            description="Az iteráció addig fut, amíg ez igaz"
          >
            <input
              type="text"
              value={node.data.config?.condition || ''}
              onChange={(e) => updateConfig('condition', e.target.value)}
              placeholder="{{counter}} < 10"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </ConfigField>
        )}

        <ConfigField
          label="Elem változó neve"
          description="Az aktuális elem elérhető lesz ezen a néven"
        >
          <input
            type="text"
            value={node.data.config?.itemVariable || 'item'}
            onChange={(e) => updateConfig('itemVariable', e.target.value)}
            placeholder="item"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>

        <ConfigField
          label="Index változó neve"
          description="Az aktuális index elérhető lesz ezen a néven"
        >
          <input
            type="text"
            value={node.data.config?.indexVariable || 'index'}
            onChange={(e) => updateConfig('indexVariable', e.target.value)}
            placeholder="index"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>
      </ConfigSection>

      <ConfigSection title="Biztonsági beállítások">
        <ConfigField
          label="Maximum iterációk"
          description="Végtelen ciklus elleni védelem"
        >
          <input
            type="number"
            value={node.data.config?.safetyMaxIterations || 1000}
            onChange={(e) => updateConfig('safetyMaxIterations', parseInt(e.target.value))}
            min="1"
            max="10000"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="breakOnError"
            checked={node.data.config?.breakOnError !== false}
            onChange={(e) => updateConfig('breakOnError', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="breakOnError" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Megszakítás hiba esetén
          </label>
        </div>
      </ConfigSection>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>💡 Használat:</strong>
          <br />• Az iteráció minden elemnél végrehajtja a következő node-okat
          <br />• Az aktuális elem: <code>{'{{' + (node.data.config?.itemVariable || 'item') + '}}'}</code>
          <br />• Az aktuális index: <code>{'{{' + (node.data.config?.indexVariable || 'index') + '}}'}</code>
          <br />• Eredmény: <code>{'{{loop_results}}'}</code> tömb
        </div>
      </div>
    </div>
  );
};

export default LoopConfig;
